import { useEffect, useRef, useState } from 'react';

interface UseCurrentTimeOptions {
    intervalMs?: number;
    getValue?: () => number;
}

export const useCurrentTime = ({
    intervalMs = 60000,
    getValue = () => new Date().getMinutes(),
}: UseCurrentTimeOptions = {}) => {
    const [value, setValue] = useState(getValue);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const getValueRef = useRef(getValue);
    const intervalMsRef = useRef(intervalMs);
    getValueRef.current = getValue;
    intervalMsRef.current = intervalMs;

    useEffect(() => {
        const stop = () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            if (intervalRef.current) clearInterval(intervalRef.current);
            timeoutRef.current = null;
            intervalRef.current = null;
        };

        const start = () => {
            stop();
            setValue(getValueRef.current());

            const msToNext = intervalMsRef.current - (Date.now() % intervalMsRef.current);

            timeoutRef.current = setTimeout(() => {
                setValue(getValueRef.current());
                intervalRef.current = setInterval(() => setValue(getValueRef.current()), intervalMsRef.current);
            }, msToNext);
        };

        const handleVisibilityChange = () => {
            document.visibilityState === 'visible' ? start() : stop();
        };

        start();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            stop();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    return value;
};
